<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import { shareOnFacebook, shareOnX } from '@utils/social-share'
import { useAppearanceStore } from '@stores/appearance'
import { useMessages } from '@composables/use-messages'

const props = defineProps({
    genre: {
        type: Object,
        default: () => {},
    },
    hasViewGenreOption: {
        type: Boolean,
        default: true,
    },
    isFollowing: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['toggle-follow'])

const appearanceStore = useAppearanceStore()
const router = useRouter()
const getMessageText = useMessages()

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    if (!props.genre.slug) return ''

    const href = router.resolve({ name: 'genre', params: { slug: props.genre.slug } }).href
    return siteUrl(href)
})

const header = computed(() => ({ title: props.genre.name, subtitle: 'Genre', image: props.genre, imageType: 'artwork' }))

// V9: the hero and grid tile own the follow toggle (and its copy); the menu forwards it.
function handleToggleFollow() {
    emit('toggle-follow', props.genre)
}

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}

// The View row is hidden without a slug: its router-link would throw resolving the param.
const items = computed(() => [
    { id: 'view', label: 'View Genre', to: { name: 'genre', params: { slug: props.genre.slug } }, hidden: !props.hasViewGenreOption || !props.genre.slug },
    { divider: true },
    { id: 'follow-genre', label: props.isFollowing ? 'Following' : 'Follow Genre', action: handleToggleFollow },
    { divider: true },
    // Interim (R8): today's three share actions as plain rows. The single `Share ›` row
    // lands with the Share surface ticket 1218209213218817.
    { id: 'share-facebook', label: 'Facebook', action: () => shareOnFacebook(url.value) },
    { id: 'share-x', label: 'X', action: () => shareOnX(url.value, `${props.genre.name} on @friskyradio #feelinfrisky`) },
    { id: 'copy-link', label: 'Copy Genre Link', action: copyLinkToClipBoard },
])
</script>

<template>
    <BaseContextMenu
        class="genre-options"
        :items="items"
        :header="header"
        trigger-label="Genre options"
    >
        <template
            v-if="$slots.trigger"
            #trigger="slotProps"
        >
            <slot
                name="trigger"
                v-bind="slotProps"
            />
        </template>
    </BaseContextMenu>
</template>
